import { FormEvent, useEffect, useMemo, useState } from 'react';
import { AppLayout } from '../components/layout/AppLayout';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import {
  createStakeSnapshot,
  deleteStakeSnapshot,
  listStakeSnapshots,
  updateStakeSnapshot,
} from '../services/stakeSnapshotsService';
import { StakeSnapshot } from '../types/stakeSnapshot';
import { formatDate } from '../utils/format';

type FormState = {
  effective_date: string;
  gaurav_pct: string;
  sourabh_pct: string;
  note: string;
};

const initialFormState = (): FormState => ({
  effective_date: new Date().toISOString().slice(0, 10),
  gaurav_pct: '40',
  sourabh_pct: '60',
  note: '',
});

export const StakeSnapshotsPage = () => {
  const { token } = useAuth();
  const { showToast } = useToast();
  const [snapshots, setSnapshots] = useState<StakeSnapshot[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [formState, setFormState] = useState<FormState>(initialFormState);
  const [editingSnapshot, setEditingSnapshot] = useState<StakeSnapshot | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; snapshotId: number | null }>({
    isOpen: false,
    snapshotId: null,
  });

  const loadSnapshots = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await listStakeSnapshots(token);
      setSnapshots(response.data);
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to load stake snapshots.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSnapshots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleInputChange = (field: keyof FormState, value: string) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEdit = (snapshot: StakeSnapshot) => {
    setEditingSnapshot(snapshot);
    setFormState({
      effective_date: snapshot.effective_date,
      gaurav_pct: snapshot.gaurav_pct,
      sourabh_pct: snapshot.sourabh_pct,
      note: snapshot.note ?? '',
    });
  };

  const resetForm = () => {
    setEditingSnapshot(null);
    setFormState(initialFormState());
  };

  const percentSum = useMemo(() => {
    const g = Number(formState.gaurav_pct || 0);
    const s = Number(formState.sourabh_pct || 0);

    return Math.round((g + s) * 100) / 100;
  }, [formState]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!token) return;

    const payload = {
      effective_date: formState.effective_date,
      gaurav_pct: Number(formState.gaurav_pct),
      sourabh_pct: Number(formState.sourabh_pct),
      company_pct: 0,
      note: formState.note || null,
    };

    if (Number.isNaN(payload.gaurav_pct) || Number.isNaN(payload.sourabh_pct)) {
      showToast('Percentages must be numeric values.', 'error');
      return;
    }

    if (Math.abs(payload.gaurav_pct + payload.sourabh_pct - 100) > 0.01) {
      showToast('Gaurav + Sourabh must equal 100%.', 'error');
      return;
    }

    setActionLoading(editingSnapshot?.id ?? 0);

    try {
      if (editingSnapshot) {
        await updateStakeSnapshot(token, editingSnapshot.id, payload);
        showToast('Stake snapshot updated successfully.', 'success');
      } else {
        await createStakeSnapshot(token, payload);
        showToast('Stake snapshot created successfully.', 'success');
      }

      resetForm();
      await loadSnapshots();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Unable to save stake snapshot.', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = (snapshot: StakeSnapshot) => {
    setDeleteConfirm({ isOpen: true, snapshotId: snapshot.id });
  };

  return (
    <AppLayout
      title="Stake Snapshots"
      toolbar={
        <div className="pill" style={{ background: 'rgba(59,130,246,0.12)', color: '#1d4ed8' }}>
          {snapshots.length} snapshots
        </div>
      }
    >

      <section className="card" style={{ padding: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <h3 style={{ margin: 0 }}>{editingSnapshot ? 'Edit stake snapshot' : 'Add stake snapshot'}</h3>
          {editingSnapshot && (
            <button
              type="button"
              className="btn btn-secondary"
              style={{ width: 'auto', padding: '10px 16px', marginTop: 0 }}
              onClick={resetForm}
            >
              Cancel edit
            </button>
          )}
        </div>
        <form
          onSubmit={handleSubmit}
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 18 }}
        >
          <div className="form-group">
            <label htmlFor="snapshot-date">Effective date</label>
            <input
              id="snapshot-date"
              type="date"
              className="input"
              value={formState.effective_date}
              onChange={(event) => handleInputChange('effective_date', event.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="snapshot-gaurav">Gaurav %</label>
            <input
              id="snapshot-gaurav"
              type="number"
              className="input"
              min="0"
              max="100"
              step="0.01"
              value={formState.gaurav_pct}
              onChange={(event) => handleInputChange('gaurav_pct', event.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="snapshot-sourabh">Sourabh %</label>
            <input
              id="snapshot-sourabh"
              type="number"
              className="input"
              min="0"
              max="100"
              step="0.01"
              value={formState.sourabh_pct}
              onChange={(event) => handleInputChange('sourabh_pct', event.target.value)}
              required
            />
          </div>
          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label htmlFor="snapshot-note">Note</label>
            <input
              id="snapshot-note"
              type="text"
              className="input"
              placeholder="Optional notes"
              value={formState.note}
              onChange={(event) => handleInputChange('note', event.target.value)}
            />
          </div>
          <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span className="subtle-text">Combined stake: {percentSum}%</span>
            <button className="btn" type="submit" disabled={actionLoading !== null}>
              {actionLoading !== null ? 'Saving…' : editingSnapshot ? 'Update snapshot' : 'Create snapshot'}
            </button>
          </div>
        </form>
      </section>

      <section className="card" style={{ padding: 0 }}>
        <div className="table">
          <div className="table-head" style={{ gridTemplateColumns: '140px 120px 120px 1.4fr 180px 160px' }}>
            <span>Effective</span>
            <span>Gaurav %</span>
            <span>Sourabh %</span>
            <span>Note</span>
            <span>Created by</span>
            <span style={{ textAlign: 'right' }}>Actions</span>
          </div>
          {loading ? (
            <>
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="table-row" style={{ gridTemplateColumns: '140px 120px 120px 1.4fr 180px 160px', alignItems: 'center' }}>
                  <LoadingSkeleton count={1} height="20px" width="100%" />
                  <LoadingSkeleton count={1} height="20px" width="100%" />
                  <LoadingSkeleton count={1} height="20px" width="100%" />
                  <LoadingSkeleton count={1} height="20px" width="100%" />
                  <LoadingSkeleton count={1} height="20px" width="100%" />
                  <LoadingSkeleton count={1} height="20px" width="100%" />
                </div>
              ))}
            </>
          ) : snapshots.length === 0 ? (
            <div className="table-row" style={{ gridTemplateColumns: '1fr', padding: '40px 20px' }}>
              <span style={{ textAlign: 'center', color: 'var(--neutral-600)' }}>
                No stake snapshots recorded yet. Create your first snapshot above.
              </span>
            </div>
          ) : (
            snapshots.map((snapshot) => (
              <div
                className="table-row"
                key={snapshot.id}
                style={{ gridTemplateColumns: '140px 120px 120px 1.4fr 180px 160px', alignItems: 'center' }}
              >
                <span>{formatDate(snapshot.effective_date)}</span>
                <span>{Number(snapshot.gaurav_pct).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                <span>{Number(snapshot.sourabh_pct).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                <span>{snapshot.note || '—'}</span>
                <span>{snapshot.created_by?.name ?? '—'}</span>
                <span style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ width: 'auto', padding: '6px 12px', fontSize: '0.85rem' }}
                    onClick={() => handleEdit(snapshot)}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{
                      width: 'auto',
                      padding: '6px 12px',
                      fontSize: '0.85rem',
                      color: '#dc2626',
                      borderColor: 'rgba(239,68,68,0.24)',
                    }}
                    disabled={actionLoading === snapshot.id}
                    onClick={() => handleDelete(snapshot)}
                  >
                    Delete
                  </button>
                </span>
              </div>
            ))
          )}
        </div>
      </section>

      {deleteConfirm.isOpen && (
        <ConfirmDialog
          isOpen={deleteConfirm.isOpen}
          title="Delete Stake Snapshot"
          message={`Are you sure you want to delete the stake snapshot effective ${deleteConfirm.snapshotId ? formatDate(snapshots.find(s => s.id === deleteConfirm.snapshotId)?.effective_date || '') : ''}? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
          onConfirm={async () => {
            if (!token || !deleteConfirm.snapshotId) return;
            setActionLoading(deleteConfirm.snapshotId);
            try {
              await deleteStakeSnapshot(token, deleteConfirm.snapshotId);
              showToast('Stake snapshot deleted successfully.', 'success');
              if (editingSnapshot?.id === deleteConfirm.snapshotId) {
                resetForm();
              }
              setDeleteConfirm({ isOpen: false, snapshotId: null });
              await loadSnapshots();
            } catch (err) {
              showToast(err instanceof Error ? err.message : 'Unable to delete stake snapshot.', 'error');
              setDeleteConfirm({ isOpen: false, snapshotId: null });
            } finally {
              setActionLoading(null);
            }
          }}
          onCancel={() => setDeleteConfirm({ isOpen: false, snapshotId: null })}
        />
      )}
    </AppLayout>
  );
};

